/**
 * Sidebar — role-aware navigation.
 *
 * ADMIN / GA   : full nav (assets, labels, storage, employees, admin)
 * FINANCE      : assets (read), dashboard
 * EXECUTIVE    : dashboard, assets (read)
 * VIEWER       : my assets only
 */

import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ROLES, ROLE_LABELS } from '../../utils/constants';
import { cn } from '../../utils/cn';

const Icon = ({ path, className }) => (
  <svg className={cn('h-5 w-5 shrink-0', className)} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

const ICONS = {
  finance: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  dashboard: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  assets: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
  qr: 'M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 3.5a.5.5 0 11-1 0 .5.5 0 011 0z',
  storage: 'M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4',
  employees: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
  settings: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  myassets: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
};

function NavItem({ to, icon, label, end = false }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary-50 text-primary-700'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
        )
      }
    >
      <Icon path={ICONS[icon]} />
      {label}
    </NavLink>
  );
}

function NavGroup({ label, children }) {
  return (
    <div className="mb-4">
      <p className="px-3 mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
        {label}
      </p>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

export default function Sidebar({ onClose }) {
  const { user, logout } = useAuth();
  if (!user) return null;

  const role = user.role;
  const isAdmin = role === ROLES.ADMIN;
  const isGA = role === ROLES.GA;
  const isFinance = role === ROLES.FINANCE;
  const isExecutive = role === ROLES.EXECUTIVE;
  const isViewer = role === ROLES.VIEWER;

  return (
    <div className="flex flex-col h-full bg-white border-r border-slate-200 w-64 shrink-0">
      {/* Logo */}
      <div className="px-5 py-4 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-primary-600 flex items-center justify-center">
            <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d={ICONS.assets} />
            </svg>
          </div>
          <span className="font-semibold text-slate-900 text-sm">Rhaya Inventory</span>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {/* Everyone gets dashboard */}
        <NavGroup label="Overview">
          <NavItem to="/" icon="dashboard" label="Dashboard" end />
          {(isAdmin || isGA || isFinance || isExecutive) && (
            <NavItem to="/finance" icon="finance" label="Finance" />
          )}
        </NavGroup>

        {/* Assets — visible to all, Viewer sees only theirs */}
        {(isAdmin || isGA || isFinance || isExecutive) && (
          <NavGroup label="Assets">
            <NavItem to="/assets" icon="assets" label="All Assets" />
            {(isAdmin || isGA) && (
              <NavItem to="/labels" icon="qr" label="Print Labels" />
            )}
            {(isAdmin || isGA) && (
              <NavItem to="/storage" icon="storage" label="Storage Units" />
            )}
          </NavGroup>
        )}

        {isViewer && (
          <NavGroup label="Assets">
            <NavItem to="/my-assets" icon="myassets" label="My Assets" />
          </NavGroup>
        )}

        {/* HR data — admin/GA only */}
        {(isAdmin || isGA) && (
          <NavGroup label="People">
            <NavItem to="/employees" icon="employees" label="Employees" />
          </NavGroup>
        )}

        {/* Admin */}
        {isAdmin && (
          <NavGroup label="System">
            <NavItem to="/admin" icon="settings" label="Admin Settings" />
          </NavGroup>
        )}
      </nav>

      {/* User footer */}
      <div className="border-t border-slate-200 px-3 py-3">
        <div className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-50 group">
          <div className="h-8 w-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold shrink-0">
            {user.name?.charAt(0)?.toUpperCase() ?? '?'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{user.name}</p>
            <p className="text-xs text-slate-500 truncate">{ROLE_LABELS[user.role] ?? user.role}</p>
          </div>
          <button
            onClick={logout}
            title="Log out"
            className="text-slate-400 hover:text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
